'use client';

import { useRef, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import { builder } from '@builder.io/react';
import useGetOffering from 'hooks/useGetOffering';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useCreateLiveStreamRequest from 'hooks/useCreateLiveStreamRequest';
import useAddLiveStreamRequestQuestion from 'hooks/useAddLiveStreamRequestQuestion';
import { trackEvent } from 'utils/analytics';
import { mapRegulationTypeToCanonical } from 'utils/offerings/helpers';
import {
  Card,
  HeadingTitle,
  Description,
  RequestButton,
  RequestedConfirmation,
  ConfirmationIcon,
  ConfirmationText,
  RequestError,
} from './styles';

const RequestLiveStreamModal = dynamic(() => import('./RequestLiveStreamModal'), { ssr: false });

const RequestLiveStreamCard = ({ 'data-cy': dataCy = 'request-live-stream-button', ...props }) => {
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const [requestError, setRequestError] = useState(false);

  const params = useParams();
  const searchParams = useSearchParams();
  const slug = params?.slug?.[0];
  const { data: offeringData } = useGetOffering({ slug });
  const signedIn = useIsSignedIn();

  const isBuilderEditing =
    builder?.isEditing ||
    builder?.isPreviewing ||
    searchParams?.get('__builder_editing__') === 'true' ||
    searchParams?.has('builder.frameEditing');

  const createRequest = useCreateLiveStreamRequest();
  const addQuestion = useAddLiveStreamRequestQuestion();
  const createdRequestRef = useRef(null);

  const isOpenOffering = offeringData?.status === 'approved';
  const isRegD = mapRegulationTypeToCanonical(offeringData?.regulationType) === 'private';
  const displayName = offeringData?.name || 'the company';

  const handleRequest = async () => {
    if (createRequest.isPending) return;
    trackEvent('tapped_request_live_stream', {
      offering_id: offeringData?.id,
      offering_name: offeringData?.name,
      offering_slug: offeringData?.slug,
      investment_type: 'invest',
      offering_regulation_type: offeringData?.regulationType,
      regulation_type: mapRegulationTypeToCanonical(offeringData?.regulationType),
      offering_type: offeringData?.offeringType ?? 'unknown',
      offering_status: offeringData?.status ?? 'unknown',
    });
    setRequestError(false);
    try {
      const created = await createRequest.mutateAsync(String(offeringData?.id));
      createdRequestRef.current = created;
      setRequested(true);
      setOpen(true);
    } catch {
      setRequestError(true);
    }
  };

  const handleClose = () => setOpen(false);

  const handleQuestionSubmit = async (question) => {
    const created = createdRequestRef.current;
    if (!created?.id) return false;
    try {
      await addQuestion.mutateAsync({ id: created.id, question });
      return true;
    } catch {
      return false;
    }
  };

  if (!isBuilderEditing && (!isOpenOffering || isRegD || !signedIn)) return null;

  return (
    <Card data-cy="request-live-stream-card">
      <HeadingTitle>Have more questions?</HeadingTitle>
      <Description>
        {`Request a live stream from ${displayName}. Streams play live in the StartEngine app, where you can watch and ask questions. Get the app to join.`}
      </Description>
      {requested ? (
        <RequestedConfirmation data-cy="request-live-stream-requested">
          <ConfirmationIcon />
          <ConfirmationText>Live stream requested</ConfirmationText>
        </RequestedConfirmation>
      ) : (
        <>
          <RequestButton
            type="button"
            data-cy={dataCy}
            onClick={handleRequest}
            disabled={createRequest.isPending}
            {...props}
          >
            {createRequest.isPending ? 'Requesting…' : 'Request a live stream'}
          </RequestButton>
          {requestError ? (
            <RequestError role="alert" data-cy="request-live-stream-error">
              Something went wrong sending your request. Please try again.
            </RequestError>
          ) : null}
        </>
      )}
      {requested ? (
        <RequestLiveStreamModal
          open={open}
          onClose={handleClose}
          onSubmitQuestion={handleQuestionSubmit}
          companyName={offeringData?.name}
          offeringId={offeringData?.id}
        />
      ) : null}
    </Card>
  );
};

export default RequestLiveStreamCard;
